import React from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Warning, Error, Info, CheckCircle, Close } from "@mui/icons-material";
import { useModalStyles } from "./useModalStyles";
import Button from "./Button";

export type WarningPopupNewVariant = "warning" | "error" | "info" | "success";

export interface WarningPopupNewButton {
  name: string;
  color: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  onClick: (obj?: any) => void;
  variant?: "contained" | "outlined" | "text";
  icon?: React.ReactNode;
}

export type WarningPopupNewObjectProps = {
  show: boolean;
  message?: React.ReactNode;
  variant?: WarningPopupNewVariant;
  buttons?: WarningPopupNewButton[];
  title?: string;
  icon?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  customBackground?: string;
  obj?: any;
};

export interface WarningPopupNewProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  onSubmit?: (obj?: any) => void;
  message?: React.ReactNode;
  variant?: WarningPopupNewVariant;
  buttons?: WarningPopupNewButton[];
  icon?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  customBackground?: string;
  obj?: any;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const WarningPopupNew: React.FC<WarningPopupNewProps> = ({
  open,
  onClose,
  title = "Warning",
  message = "Please review the information below.",
  variant = "warning",
  buttons = [{ name: "OK", color: "primary", onClick: onClose }],
  icon,
  maxWidth = "xs",
  showCloseIcon = true,
  obj,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    tokens,
    paperSx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const getVariantConfig = (v: WarningPopupNewVariant) => {
    switch (v) {
      case "error":
        return {
          Icon: Error,
          accentColor: "#EF4444",
          iconBg: "rgba(239,68,68,0.12)",
          iconRing: "rgba(239,68,68,0.25)",
          topBar: "#EF4444",
          label: "Error",
        };
      case "success":
        return {
          Icon: CheckCircle,
          accentColor: "#22C55E",
          iconBg: "rgba(34,197,94,0.12)",
          iconRing: "rgba(34,197,94,0.25)",
          topBar: "#22C55E",
          label: "Success",
        };
      case "info":
        return {
          Icon: Info,
          accentColor: "#3B82F6",
          iconBg: "rgba(59,130,246,0.12)",
          iconRing: "rgba(59,130,246,0.25)",
          topBar: "#3B82F6",
          label: "Info",
        };
      default:
        return {
          Icon: Warning,
          accentColor: "#F59E0B",
          iconBg: "rgba(245,158,11,0.12)",
          iconRing: "rgba(245,158,11,0.25)",
          topBar: "#F59E0B",
          label: "Warning",
        };
    }
  };

  const cfg = getVariantConfig(variant);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      {/* ── Close button ── */}
      {showCloseIcon && (
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <Close sx={{ fontSize: "0.85rem" }} />
        </IconButton>
      )}

      {/* ── Body ── */}
      <Box
        sx={{
          px: 3.5,
          pt: 4,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Centered icon with pulse ring */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          {/* Outer pulse ring */}
          <Box
            sx={{
              position: "absolute",
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: `1px solid ${cfg.iconRing}`,
              animation: "ringPulse 2.5s ease-in-out infinite",
              "@keyframes ringPulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 0.6 },
                "50%": { transform: "scale(1.12)", opacity: 0.15 },
              },
            }}
          />
          {/* Icon circle — uses iconBoxSx style from modal */}
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: cfg.iconBg,
              border: `1.5px solid ${cfg.iconRing}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            {icon ?? <cfg.Icon sx={{ fontSize: "1.6rem", color: cfg.accentColor }} />}
          </Box>
        </Box>

        {/* Variant label pill */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.2,
            py: 0.3,
            borderRadius: "999px",
            backgroundColor: cfg.iconBg,
            border: `1px solid ${cfg.iconRing}`,
            mb: 1.2,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: cfg.accentColor,
              textTransform: "uppercase",
            }}
          >
            {cfg.label}
          </Typography>
        </Box>

        {/* Title — uses tokens.textPrimary */}
        <Typography
          fontWeight={700}
          sx={{
            fontSize: "1.05rem",
            color: tokens.textPrimary,
            lineHeight: 1.35,
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/* Message — uses tokens.textSecondary */}
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.855rem",
            color: tokens.textSecondary,
            lineHeight: 1.7,
            maxWidth: 320,
          }}
        >
          {message}
        </Typography>
      </Box>

      {/* ── Divider — uses tokens.border ── */}
      <Box
        sx={{
          mx: 3.5,
          height: "1px",
          backgroundColor: tokens.border,
          flexShrink: 0,
        }}
      />

      {/* ── Footer — uses footerSx from modal ── */}
      <Box sx={{ ...footerSx, backgroundColor: tokens.headerFooterBg }}>
        <Grid container spacing={1.5} justifyContent="center">
          {[...buttons].reverse().map((button, index) => (
            <Grid item xs={12} sm="auto" key={index}>
              <Button
                color={button.name === "Cancel" ? "error" : button.color}
                variant={
                  button.variant ??
                  (button.name === "Cancel" ? "outlined" : "contained")
                }
                onClick={() => button.onClick(obj)}
                startIcon={button.icon}
                size="medium"
                fullWidth
                sx={{
                  ...(button.name === "Cancel" ? cancelBtnSx : primaryBtnSx),
                  minWidth: 110,
                }}
              >
                {button.name}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Dialog>
  );
};

export default WarningPopupNew;